import axios from "./axiosInstance";
import config from "./config";
import { toast } from "react-toastify";
import { captureException } from "../utils/sentry";
import { describeNotStored } from "../utils/settingsSave";

// Every settings page saves through updateSettings, so this is the one place
// that tells staff when the server left part of a save out. The message stays
// up until dismissed: it means a control on the page does not save.
const warnIfNotStored = (data) => {
  const message = describeNotStored(data?.not_stored);
  if (message) {
    toast.warn(message, { autoClose: false, toastId: "settings-not-stored" });
  }
};

const BASE_URL = config.BASE_URL;

// GET
export const getSettings = () => {
  return axios
    .get(`${BASE_URL}/settings`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST
export const updateSettings = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/settings/`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      warnIfNotStored(res?.data);
      if (res) return res?.data;
    })
    .catch((err) => {
      // A save where NOTHING could be stored comes back as a failure that
      // still names the fields.
      warnIfNotStored(err?.response?.data);
      captureException(err);
      throw err;
    });
};

// GET
export const getNotificationSettingsByRole = (id) => {
  return axios
    .get(`${BASE_URL}/settings/notification-settings/${id}`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT
export const updateNotificationSettingsByRole = (token, id, newForm) => {
  return axios
    .put(`${BASE_URL}/settings/notification-settings/${id}`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST
export const addOrUpdatePricingConfig = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/settings/program-pricing-config`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getProgramPricingConfig = () => {
  return axios
    .get(`${BASE_URL}/settings/program-pricing-config`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST
export const createMediaItem = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/settings/media`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// DEL
export const deleteMediaItem = (token, id) => {
  return axios
    .delete(`${BASE_URL}/settings/media/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};


// ===========================
// Pricing Tiers Management
// ===========================

// GET All Pricing Tiers
export const getPricingTiers = () => {
  return axios
    .get(`${BASE_URL}/settings/pricing-tiers`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST Create Pricing Tier
export const createPricingTier = (token, tierData) => {
  return axios
    .post(`${BASE_URL}/settings/pricing-tiers`, tierData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      return err?.response?.data || { success: false, error: err.message };
    });
};

// PUT Update Pricing Tier
export const updatePricingTier = (token, id, tierData) => {
  return axios
    .put(`${BASE_URL}/settings/pricing-tiers/${id}`, tierData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      return err?.response?.data || { success: false, error: err.message };
    });
};

// DELETE Pricing Tier
export const deletePricingTier = (token, id) => {
  return axios
    .delete(`${BASE_URL}/settings/pricing-tiers/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// ===========================
// Email Template Management
// ===========================

// GET Email Template by notification key and role
export const getEmailTemplate = (token, notificationKey, roleId) => {
  return axios
    .get(`${BASE_URL}/settings/email-templates`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: {
        notification_key: notificationKey,
        role_id: roleId,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// GET All Email Templates for a tenant
export const getAllEmailTemplates = (token) => {
  return axios
    .get(`${BASE_URL}/settings/email-templates/all`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET quick-reply preset list — drives the buttons in the Messages tab.
// Returns [{ label, notification_key }]. Any "Quick: ..." row in
// email_templates becomes a button; tenants can add new presets via the
// existing template editor without a code change.
export const getQuickReplyTemplates = (token) => {
  return axios
    .get(`${BASE_URL}/settings/email-templates/quick-replies`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data ?? null)
    .catch((err) => { captureException(err); return null; });
};

// POST/PUT Save Email Template
export const saveEmailTemplate = (token, templateData) => {
  return axios
    .post(`${BASE_URL}/settings/email-templates`, templateData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// DELETE Email Template
export const deleteEmailTemplate = (token, templateId) => {
  return axios
    .delete(`${BASE_URL}/settings/email-templates/${templateId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET Default Email Template (from system defaults, not tenant-specific)
export const getDefaultEmailTemplate = (token, notificationKey) => {
  return axios
    .get(`${BASE_URL}/settings/email-templates/default/${notificationKey}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// ===========================
// Email Header/Footer Management
// ===========================

// GET Email Header Template
export const getEmailHeader = (token) => {
  return axios
    .get(`${BASE_URL}/settings/email-header`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// POST/PUT Email Header Template
export const saveEmailHeader = (token, htmlContent) => {
  return axios
    .post(`${BASE_URL}/settings/email-header`, { html_content: htmlContent }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// GET Email Footer Template
export const getEmailFooter = (token) => {
  return axios
    .get(`${BASE_URL}/settings/email-footer`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// POST/PUT Email Footer Template
export const saveEmailFooter = (token, htmlContent) => {
  return axios
    .post(`${BASE_URL}/settings/email-footer`, { html_content: htmlContent }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// DELETE Email Header (revert to system default)
export const deleteEmailHeader = (token) => {
  return axios
    .delete(`${BASE_URL}/settings/email-header`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// DELETE Email Footer (revert to system default)
export const deleteEmailFooter = (token) => {
  return axios
    .delete(`${BASE_URL}/settings/email-footer`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// ===========================
// Email Template Variables
// ===========================

// GET Template Variables for a notification type
export const getTemplateVariables = (notificationKey) => {
  return axios
    .get(`${BASE_URL}/settings/email-templates/variables/${notificationKey}`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// ===========================
// Email Preview
// ===========================

// POST Get Email Preview (uses unified email construction system)
export const getEmailPreview = (token, previewData) => {
  return axios
    .post(`${BASE_URL}/settings/email-templates/preview`, previewData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// ===========================
// Email Testing
// ===========================

// POST Send Test Email
export const sendTestEmail = (token, emailData) => {
  return axios
    .post(`${BASE_URL}/settings/email-templates/test`, emailData, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return { success: false, error: err };
    });
};

// ===========================
// Quick-Reply Templates
// ===========================

// POST Render a template against a specific member's context
// Returns { success, data: { subject, body } } where body is inner HTML
// (no header/footer wrapper) ready to drop into the message composer.
export const renderTemplateForMember = (token, { notificationKey, memberId }) => {
  return axios
    .post(
      `${BASE_URL}/settings/email-templates/render-for-member`,
      { notification_key: notificationKey, member_id: memberId },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return { success: true, data: res?.data };
    })
    .catch((err) => {
      captureException(err);
      return {
        success: false,
        error: err?.response?.data?.error || err?.message || "Failed to load template",
      };
    });
};
